import {
  type TinyUser,
  type TinyCloser,
  type AvailableTimes,
  type Minutes,
  type Weekday,
  isEmpty,
  unique,
  calculateDuration,
  stringifyDuration,
  type WorkflowScheduleRestriction,
} from '@terros/common'

export type CloserTimeslot = {
  duration: number
  closer: TinyUser
}

export type TimeslotFormValues = {
  meetingDuration: number
  granularity: number
  timeslots: AvailableTimes
}

export const EMPTY_TIMES: AvailableTimes = {
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
}

export const weekDays: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export const toCloserTimeslots = (closers: TinyCloser[]): Record<number, CloserTimeslot[]> => {
  const timeslots: Record<number, CloserTimeslot[]> = {}
  closers.forEach(({ user: closer, times }) => {
    times.forEach(({ startTime, duration }) => {
      const userDuration = { duration, closer }
      if (timeslots[startTime]) {
        timeslots[startTime].push(userDuration)
      } else {
        timeslots[startTime] = [userDuration]
      }
    })
  })
  return timeslots
}

export const generateTimeSlots = (startHour: number, endHour: number, granularity: number): Minutes[] => {
  const slots: Minutes[] = []
  let currentTime = startHour * 60
  const endTime = endHour * 60

  while (currentTime < endTime) {
    slots.push(currentTime)
    currentTime += granularity
  }

  return slots
}

export const getGranularity = (availableTimes?: AvailableTimes): number => {
  if (!availableTimes) return 60

  const allTimes = unique(Object.values(availableTimes).flat()).sort((a, b) => a - b)

  if (allTimes.length < 2) return 60

  const differences: Set<number> = new Set()
  for (let i = 1; i < allTimes.length; i++) {
    const diff = allTimes[i] - allTimes[i - 1]
    if (diff > 0) {
      differences.add(diff > 60 ? diff % 60 : diff)
    }
  }

  if (isEmpty(differences)) return 60
  const smallestDifference = Math.min(...Array.from(differences))
  if (smallestDifference === 0) return 60
  return smallestDifference
}

export const formatTime = (minutes: Minutes): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`
}

export const formatTimeRange = (start: Minutes, duration: Minutes): string => {
  return `${formatTime(start)} - ${formatTime(start + duration)}`
}

export const filterValidTimeslots = (
  availableTimes: AvailableTimes,
  newGranularity: number,
  startHour: number = 7,
  endHour: number = 21
): AvailableTimes => {
  const validSlots = generateTimeSlots(startHour, endHour, newGranularity)
  const validSlotsSet = new Set(validSlots)

  const filtered: AvailableTimes = {} as AvailableTimes

  weekDays.forEach((day) => {
    filtered[day] = availableTimes[day].filter((time) => validSlotsSet.has(time))
  })

  return filtered
}

export const hasInvalidSelections = (
  availableTimes: AvailableTimes,
  newGranularity: number,
  startHour: number = 7,
  endHour: number = 21
): boolean => {
  const validSlots = generateTimeSlots(startHour, endHour, newGranularity)
  const validSlotsSet = new Set(validSlots)

  return Object.values(availableTimes).some((times) => times.some((time) => !validSlotsSet.has(time)))
}

export const isNoticeConfigValid = (minimumNotice: number | undefined, maximumNotice: number | undefined): boolean => {
  if (!minimumNotice || !maximumNotice) return true
  return minimumNotice < maximumNotice * 24
}

export const buildScheduleRestrictionMessage = (scheduleRestriction: WorkflowScheduleRestriction): string => {
  const { operator, milliseconds } = scheduleRestriction
  const duration = calculateDuration(milliseconds)
  const timeStr = stringifyDuration(duration)
  switch (operator) {
    case 'gte':
      return `Appointments must be scheduled at least ${timeStr} in advance`
    case 'lte':
      return `Appointments must be scheduled within ${timeStr}`
  }
}
